/**
 * Audio Volume Manager - Controls app-level audio volume
 * 
 * This manages the volume of all audio elements within the SARVIK app,
 * independent of the system volume.
 * 
 * Features:
 * - Control all audio elements' volume (0-100%)
 * - Persist volume setting to localStorage
 * - Automatically apply volume to new audio elements
 * - Event-based updates for UI sync
 */

import { EventEmitter } from 'events';

class AudioVolumeManager extends EventEmitter {
  constructor() {
    super();
    this.appVolume = 100; // Default 100%
    this.audioElements = new Set(); // Track all audio elements
    this.initialized = false;
    this.mutationObserver = null; // Store observer for cleanup
  }

  /**
   * Initialize the audio volume manager
   */
  initialize() {
    if (this.initialized) return;

    // Load saved app volume from localStorage
    const savedVolume = localStorage.getItem('appVolume');
    if (savedVolume) {
      this.appVolume = parseInt(savedVolume);
      console.log(`🔊 Loaded app volume: ${this.appVolume}%`);
    }

    // Listen for new audio elements being created
    this.startAudioElementMonitoring();

    this.initialized = true;
    console.log('✅ Audio volume manager initialized');
  }

  /**
   * Monitor DOM for new audio elements and apply volume
   */
  startAudioElementMonitoring() {
    // Use MutationObserver to detect new audio elements
    if (typeof document !== 'undefined') {
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName === 'AUDIO') {
              this.registerAudioElement(node);
            }
            // Check children if it's an element
            if (node.querySelectorAll) {
              node.querySelectorAll('audio').forEach((audio) => {
                this.registerAudioElement(audio);
              });
            }
          });
        });
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Apply to existing audio elements
      document.querySelectorAll('audio').forEach((audio) => {
        this.registerAudioElement(audio);
      });
    }
  }

  /**
   * Register an audio element and apply current volume
   */
  registerAudioElement(audioElement) {
    if (this.audioElements.has(audioElement)) return;

    this.audioElements.add(audioElement);
    audioElement.volume = this.appVolume / 100;

    // Remove from set when element is removed
    audioElement.addEventListener('ended', () => {
      this.audioElements.delete(audioElement);
    });
  }

  /**
   * Set app volume (0-100)
   */
  setVolume(volume) {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    this.appVolume = clampedVolume;

    // Apply to all tracked audio elements
    this.audioElements.forEach((audio) => {
      audio.volume = clampedVolume / 100;
    });

    // Save to localStorage
    localStorage.setItem('appVolume', clampedVolume.toString());

    // Emit event for UI updates
    this.emit('volumeChanged', clampedVolume);
  }

  /**
   * Get current app volume
   */
  getVolume() {
    return this.appVolume;
  }

  /**
   * Apply volume to a specific audio element
   * Useful for dynamically created audio
   */
  applyToAudioElement(audioElement) {
    if (audioElement && audioElement.nodeName === 'AUDIO') {
      audioElement.volume = this.appVolume / 100;
      this.registerAudioElement(audioElement);
    }
  }

  /**
   * Create and play an audio element with app volume applied
   */
  playAudio(audioSrc, options = {}) {
    const audio = new Audio(audioSrc);
    
    // Apply app volume
    audio.volume = this.appVolume / 100;
    
    // Apply any additional options
    if (options.loop) audio.loop = options.loop;
    if (options.autoplay !== false) audio.autoplay = true;
    
    // Register for volume updates
    this.registerAudioElement(audio);
    
    // Play
    audio.play().catch(err => {
      console.error('Failed to play audio:', err);
    });
    
    return audio;
  }

  /**
   * Cleanup
   */
  cleanup() {
    // Disconnect MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    
    // Clear tracked audio elements
    this.audioElements.clear();
    
    // Remove all event listeners
    this.removeAllListeners();
    
    console.log('🧹 Audio volume manager cleaned up');
  }
}

// Export singleton instance
export const audioVolumeManager = new AudioVolumeManager();
